<template>
    <wrapper-features-templates-operations
    :showLoader="showLoader"
    :showOverlayLoader="showOverlayLoader"
    :applyErrorState="applyErrorState && !showLoader"
    :hideContentWrapper="!showLoader && !applyErrorState && !getAccountsBalances.length && !getCards.length"
    :feedbackOperationData="feedbackOperationData"
    :activeStep="activeStep"
    :scrollValue="scrollValue"
    :stepperParams="stepperParams"
    :showStepper="activeStep <= stepperParams.length && !applyErrorState && !showLoader"
    :showFooter="activeStep < 3 && !showLoader && !applyErrorState"
    :footerButtonsParams="footerButtonsParams.filter(obj => obj.step === activeStep)"
    :tokenTitleAppend="t('cards.charge.mcx.tokenTitleAppend')"
    @challengeSuccessHandler="successHandler"
    @challengeErrorHandler="errorHandler($event, chargeCardHandler)"
    @clearScrollToValue="scrollValue = null"
    @backOrCloseClicked="navigateBack()">
        <component
        :is="getActiveStepComponent"
        :getCards="getCards"
        :getAccountsBalances="getAccountsBalances"
        :clearFormErrors="clearFormErrors"
        :formData="formData"
        :mandatoryList="mandatoryList"
        :showMandatoryErrors="showMandatoryErrors"
        :responseData="responseData"
        @newCharge="newChargeHandler"
        @openReuseChargeModal="showReuseTransferModal = true"
        @updateFormData="updateFormDataHandler"
        @formErrorsCleared="clearFormErrors = false"
        @resetShowMandatoryErrorsProp="showMandatoryErrors = false"
        @scrollTo="scrollValue = $event"
        @showOverlayLoader="showOverlayLoader = true"
        @hideOverlayLoader="showOverlayLoader = false"/>
        <!-- Empty-state -->
        <template #empty-state-slot>
            <transition name="common-enter-animation" mode="out-in" appear>
                <empty-state-generic
                v-if="!applyErrorState && !showLoader && !getCards.length"
                illustration="emptyState/cards-mcx"
                :title="t('cards.charge.mcx.emptyState.title')"
                :description="t('cards.charge.mcx.emptyState.description')"/>
            </transition>
        </template>
    </wrapper-features-templates-operations>
    <!-- Feedback Toasts -->
    <toast-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOperations from '@/components/wrappers/featuresTemplates/Operations'
import ToastGeneric from '@/components/generic/toasts/Generic'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'

/* Vue */
import { ref, computed, defineAsyncComponent, onBeforeUnmount, watch } from 'vue'

/* Assets & Helpers */
import { atLeastOneIsEmpty, clearObjectStrings } from '@/assets/scripts/objects'
import { errorObjectConstructor } from '@/assets/scripts/toastsHelpers'
import { navigateBack } from '@/router/helpers/navigation'

/* Store */
import { createDownloadGenericReceiptPdf } from '@/network/api/operations/operations.api.js'
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* mapActions & mapGetters */
const { requestCards, requestAccountsBalances, chargeCreditCard, clearFeatureStates } = mapActions(moduleMapper.CARDS_CHARGE)
const {	getCards, getAccountsBalances } = mapGetters(moduleMapper.CARDS_CHARGE)

/* Data */
const showLoader = ref(false)
const showOverlayLoader = ref(false)
const applyErrorState = ref(false)
const scrollValue = ref(null)
const showMandatoryErrors = ref(false)
const clearFormErrors = ref(false)

/*************************************************
*                                                *
*                    Steps Data                  *
*                                                *
*************************************************/
const activeStep = ref(1)
const stepperParams = ref([
    { description: t('cards.charge.mcx.stepperParams.data') },
    { description: t('cards.charge.mcx.stepperParams.validation') }
])

const goToStep = stepNumber => {
    activeStep.value = stepNumber
    scrollValue.value = null
}

const getActiveStepComponent = computed(() => {
    const stepNumber = activeStep.value
    return defineAsyncComponent(() => import('@/components/features/cards/charge/mcx/Step' + stepNumber))
})

/*************************************************
*                                                *
*                    Form Data                   *
*                                                *
*************************************************/
const formData = ref({
    originAccount: '',
    originCard: '',
    currency: '',
    amount: ''

})

const mandatoryList = ref([
    'originAccount',
    'originCard',
    'amount'
])

const updateFormDataHandler = ({ field, inputValue }) => {
    formData.value[field] = inputValue
}

/*************************************************
*                                                *
*                 Requests Logic                 *
*                                                *
*************************************************/
const initialRequests = async () => {
	showLoader.value = true
    applyErrorState.value = false

	try {
        await requestCards()
        await requestAccountsBalances()

        if (getCards.value.length) {
            formData.value.originCard = getCards.value[0].id
            formData.value.currency = getCards.value[0].currency
        }
        if (getAccountsBalances.value.length) {
            formData.value.originAccount = getAccountsBalances.value[0].id
        }

		showLoader.value = false
	} catch (error) {
        if (error.toString() === 'Cancel') return
        applyErrorState.value = true
        errorHandler(error.response.data, initialRequests)
	}
}

const chargeCardHandler = async () => {
	showOverlayLoader.value = true
	showFeedbackToast.value = false
	applyErrorState.value = false

	const payload = {
		cardId: formData.value.originCard,
		data: {
			originAccount: formData.value.originAccount,
            amount: formData.value.amount,
			note: ''
		}
	}

	try {
		await chargeCreditCard(payload)
		showOverlayLoader.value = false
	} catch (error) {
		errorHandler(error.response.data, chargeCardHandler)
	}
}

/*************************************************
*                                                *
*             Success/Error Handlers             *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackOperationData = ref(null)
const feedbackToastData = ref({
	toastType: '',
	text: '',
	buttons: []
})
const responseData = ref(null)

const successHandler = data => {
    responseData.value = data

    feedbackOperationData.value = {
        status: data.errorLevel,
        title: data.message,
        buttonsData: !data.data.hasReceipt ? null : {
            iconsColor: data.errorLevel === 'INFO' ? 'var(--positive)' : 'var(--warning)',
            emailAction: 'sendOperationEmailNotification',
            smsAction: 'sendOperationSMSNotification',
            sendPayload: { operationId: data.data.operationId },
            downloadUrl: createDownloadGenericReceiptPdf({ params: { operationID: data.data.operationId } }),
            operationId: data.data.operationId
        }
    }

    goToStep(3)
}

const errorHandler = (data, retryFn) => {
	feedbackToastData.value = errorObjectConstructor(data, retryFn, () => { activeStep.value = 1 })

    showLoader.value = false
    showOverlayLoader.value = false
	showFeedbackToast.value = true
}

/*************************************************
*                                                *
*              Footer Buttons Data               *
*                                                *
*************************************************/
const footerButtonsParams = computed(() => {
    return [{
        label: t('cards.charge.mcx.footerButtonsParams.clear'),
        callbackFunction: clearInputs,
        buttonPosition: 'left',
        step: 1
    }, {
        label: t('cards.charge.mcx.footerButtonsParams.continue'),
        callbackFunction: validateInputs,
        icon: 'arrow-right-full',
        iconPosition: 'right',
        buttonType: 'primary',
        step: 1
    }, {
        label: t('cards.charge.mcx.footerButtonsParams.goBack'),
        icon: 'arrow-left-full',
        callbackFunction: () => goToStep(1),
        step: 2
    },
    {
        label: t('cards.charge.mcx.footerButtonsParams.validate'),
        callbackFunction: chargeCardHandler,
        icon: 'arrow-right-full',
        iconPosition: 'right',
        buttonType: 'primary',
        step: 2
    }]
})

const validateInputs = () => {
    formData.value.amount <= 0 || atLeastOneIsEmpty(formData.value, mandatoryList.value) ? showMandatoryErrors.value = true : goToStep(2)
}

const clearInputs = () => {
    clearFormErrors.value = true
    clearObjectStrings(formData.value)

    /* Setting Initial Selected Card and Currency */
    formData.value.originCard = getCards.value[0].id
    formData.value.currency = getCards.value[0].currency
    /* Setting Initial Selected Account */
    formData.value.originAccount = getAccountsBalances.value[0].id

    feedbackOperationData.value = null
    activeStep.value = 1
    responseData.value = null
}

const newChargeHandler = () => {
    clearInputs()
    initialRequests()
}

watch(() => activeStep.value, (newVal, oldVal) => {
    scrollValue.value = 0
})

initialRequests()

onBeforeUnmount(() => {
    clearFeatureStates()
})
</script>

<style lang="scss" scoped>
</style>
